// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import omitBy from 'lodash/omitBy';

import {
  Entry,
  isCohortEntry,
  isEventBasedEntry,
  isEventEntry,
  isFormulaEntry,
  isGroupedEntry,
  isInlineFormulaEntry,
  isMetricEntry,
  isUserBasedEntry,
  MetricEntry,
  QueryEntry,
  QueryEventEntry,
  QueryFilterEntry,
  QuerySegmentEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ResourceType} from 'common/report/constants';
import {getReportName} from 'common/report/util';
import {
  FUNNELS_MATH_TYPES,
  InsightsResourceType,
  MATH_TYPE,
  MetricType,
  MultiAttributionType,
  PRIMITIVE_MATH_TYPES,
  TopLevelMetricType,
  XAU_MATH_TYPES,
} from 'common/types/reports/bookmark';
import {
  EventSegment,
  isCohortSegment,
  isEventSegment,
  isExperimentSegment,
  isUserSegment,
  OtherSegmentation,
  Segment,
  SegmentValue,
} from 'common/query-builder/query-block/segment-menu/types';
import {ButtonTheme} from 'common/components/button/constants';
import {Segmentation} from 'common/types/reports/query';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {isBehaviorFilter, isCohortFilter, isUserFilter} from 'common/widgets/property-filter-menu/models/utils';
import {
  isAggregateUserFreqByGroupSegmentation,
  isBehaviorSegmentation,
  isMultiAttributionSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {BEHAVIOR_MULTI_ATTRIBUTION_FILTER} from 'common/query-builder/query-block/properties-menu/constants';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import {getEntries} from 'common/report/insights/util/events';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {PropertyValue} from 'common/query-builder/query-block/properties-menu/types';
import {BEHAVIOR_METRIC_TYPES} from 'common/report/insights/models/constants';

import {getEntryDataGroupId} from './data-groups';

export const queryBuilderUtils = {
  isCoreReport,
};

/**
 * Sanitizes exclusionSteps by fixing out of range step selections
 */
export function sanitizeExclusionSteps(exclusionSteps: QueryEventEntry[], eventsCount: number): QueryEventEntry[] {
  return exclusionSteps.map((step) => {
    if (`to` in step.excludeRange && (step.excludeRange as {from: number; to: number}).to > eventsCount) {
      step.excludeAll = true;
      step.excludeRange = null;
    }

    return step;
  });
}

export function transformToGlobalQueryFilterEntries(filters: Array<Partial<PropertyFilter>>): QueryFilterEntry[] {
  return filters.map((filterItem) => ({
    event: filterItem,
    // global filters do not render and/or
    filtersOperator: null,
    filters: [],
    type: SelectorTypeDefinitions.Filter,
  }));
}

// The format used by mp-query-builder and mp-query-entry-section are different. So it's necessary to map
// between these two formats.
export function transformToQuerySegmentEntries(segments: Segment[]): QuerySegmentEntry[] {
  return cloneDeep(segments).map((segment) => {
    let customBucket;
    let isHidden;
    let segmentValue: SegmentValue;

    if (isCohortSegment(segment)) {
      segmentValue = segment;
    } else if (Array.isArray(segment)) {
      // Value comparisons shouldnt have custom bucketing so dont need to remove
      segmentValue = segment.map((seg) => Object.values(seg)[0]);
    } else {
      const segmentation: Segmentation = Object.values(segment)[0];

      // The QuerySegmentEntry format has some fields as top-level fields. So we remove them
      // from the segment and move it to be a top-level field.
      customBucket = segmentation.customBucket;
      isHidden = segmentation.isHidden;
      delete segmentation.customBucket;
      delete segmentation.isHidden;

      segmentValue = segmentation;
    }

    return {
      event: segmentValue,
      filters: [],
      type: SelectorTypeDefinitions.Segment,
      ...(customBucket ? {customBucket} : {}),
      ...(isHidden ? {isHidden} : {}),
    };
  });
}

/**
 * Insert the custom bucketing entry field into the segment fields
 * @todo [pablo] refactor this so that query-entry-section looks at the
 * segment value to see if custom bucketing is enabled or not
 */
export function transformFromQuerySegmentEntries(entries: QuerySegmentEntry[]): Segment[] {
  return entries.map((entry) => {
    if (isCohortSegment(entry.event)) {
      return entry.event;
    }

    const segmentationArray = Array.isArray(entry.event)
      ? entry.event
      : [entry.event]
          .map((event) => ({
            ...event,
            ...(entry.customBucket && !event.customBucket ? {customBucket: entry.customBucket} : {}),
            ...(entry.isHidden ? {isHidden: entry.isHidden} : {}),
          }))
          .map((segment) => omitBy(segment, (v) => v === undefined || v === null));

    const segmentations = segmentationArray.map((segment) => {
      switch (segment.resourceType) {
        case ResourceType.Event:
          return {[ResourceType.Event]: segment};
        case ResourceType.User:
          return {[ResourceType.User]: segment};
        case ResourceType.Other:
          return {[ResourceType.Other]: segment as OtherSegmentation};
        case ResourceType.Cohort:
        default:
          throw new Error(`unknown segment type: ${segment.resourceType}`);
      }
    });
    return segmentations.length === 1 ? segmentations[0] : segmentations;
  });
}

export function transformQuerySegmentEntriesToSegmentation(entries: QuerySegmentEntry[]): Segmentation[] {
  return transformFromQuerySegmentEntries(entries).map((segment) => {
    return Object.values(segment)[0];
  });
}

function hasSupportedAttributionCountType(countType: MATH_TYPE): boolean {
  return [PRIMITIVE_MATH_TYPES.TOTAL, PRIMITIVE_MATH_TYPES.UNIQUE].includes(countType as PRIMITIVE_MATH_TYPES);
}

export function hasSupportedAttributionType(type: MultiAttributionType): boolean {
  // These attribution types are compatible with all count types
  return [MultiAttributionType.LastTouch, MultiAttributionType.FirstTouch, MultiAttributionType.Participation].includes(
    type,
  );
}

export function isSupportedMultiAttributionType(countType: MATH_TYPE, type: MultiAttributionType): boolean {
  // Total Sessions is never supported.
  if (
    countType === PRIMITIVE_MATH_TYPES.SESSIONS ||
    countType === FUNNELS_MATH_TYPES.SESSION ||
    countType === FUNNELS_MATH_TYPES.CONVERSION_RATE_SESSION
  ) {
    return false;
  }

  // There are a set of attribution types that are compatible with all count
  // types and a set of count types that are compatible with all attribution
  // types. If they have one of either and they're good, if they don't then the
  // pairing is incompatible.
  return hasSupportedAttributionCountType(countType) || hasSupportedAttributionType(type);
}

/**
 * Returns true if the given query/metric entry supports the given multi-attribution type.
 *
 * If no multi attribution type is provided it will default to the default assigned
 * when adding a multi attribution breakdown.
 * */
export function isSupportedMultiAttributionTypeForEntry({
  entry,
  multiAttributionType = DefaultMultiAttribution.type,
  metricsResource,
  behaviorsResource,
}: {
  entry: Entry;
  multiAttributionType?: MultiAttributionType;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): boolean {
  if (entry.type === TopLevelMetricType.NewFormulaEntry) {
    if (!entry.referencedMetrics) {
      return false;
    }

    return entry.referencedMetrics?.every((metric) =>
      isSupportedMultiAttributionType(getMathTypeFromEntry(metric), multiAttributionType),
    );
  }

  if (isFormulaEntry(entry)) {
    if (isInlineFormulaEntry(entry)) {
      return false;
    }
    const resolvedFormula = getResolvedFormulaEntry({metricsResource, behaviorsResource, formulaEntry: entry});
    return Boolean(
      resolvedFormula?.event.referencedMetrics.every((metric) => {
        return (
          metric.behavior.resourceType === InsightsResourceType.Events &&
          isSupportedMultiAttributionType(metric.measurement.math, multiAttributionType)
        );
      }),
    );
  } else if ((isMetricEntry(entry) && BEHAVIOR_METRIC_TYPES.includes(entry.behavior.type)) || isEventEntry(entry)) {
    return isSupportedMultiAttributionType(getMathTypeFromEntry(entry), multiAttributionType);
  }
  return false;
}

export function isMultiAttributionFilter(filter: PropertyFilter): boolean {
  return filter.propertyName === BEHAVIOR_MULTI_ATTRIBUTION_FILTER.value;
}

export function isMultiAttributionSegment(segment: Segment): segment is EventSegment {
  return isEventSegment(segment) && isMultiAttributionSegmentation(segment.event);
}

function isBehaviorSegment(segment: Segment) {
  return isUserSegment(segment) && isBehaviorSegmentation(segment.user);
}

export function getMathTypeFromEntry(entry: Entry): MATH_TYPE {
  if (entry.type === TopLevelMetricType.NewFormulaEntry || isMetricEntry(entry)) {
    return entry.measurement.aggregationOperator;
  }

  return entry.aggregationOperator;
}

export function getMultiAttributionFromEntry(entry: Entry): MultiAttribution | undefined | null {
  if (entry.type === TopLevelMetricType.NewFormulaEntry || isMetricEntry(entry)) {
    return entry.measurement?.multiAttribution ?? null;
  }

  if (isEventEntry(entry)) {
    return entry.multiAttribution;
  }

  if (isFormulaEntry(entry)) {
    return entry.multiAttribution;
  }

  return null;
}

/**
 * Sanitizes group-by entries when a cohort entry is selected
 */
export function sanitizeGroupBys({
  entries,
  segments,
  metricsResource,
  behaviorsResource,
  dataGroupId,
  hasMetricLevelDataGroups,
}: {
  entries: Array<QueryEntry | MetricEntry>;
  segments: Segment[];
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
  dataGroupId: string;
  hasMetricLevelDataGroups: boolean;
}) {
  const allEntries = flattenReferencedEntries(entries, metricsResource, behaviorsResource);
  const dataGroupIds = allEntries
    .map((entry) => getEntryDataGroupId({entry}))
    .reduce((acc, dataGroupSet) => {
      if (dataGroupSet) {
        dataGroupSet.forEach((dg) => {
          acc.add(dg);
        });
      }
      return acc;
    }, new Set<string | null>());
  const nonFormulaEntries = allEntries.filter(
    (entry) => entry.type !== TopLevelMetricType.NewFormulaEntry && !isFormulaEntry(entry),
  );
  const eventsOnlyEntries = nonFormulaEntries.every((entry) => isEventBasedEntry(entry) || isGroupedEntry(entry));
  const peopleOnlyEntries = nonFormulaEntries.length > 0 && nonFormulaEntries.every((entry) => isUserBasedEntry(entry));
  const hasCohort = allEntries.length > 0 && allEntries.some((entry) => isCohortEntry(entry));
  const hasXau = nonFormulaEntries.some((entry) => isXauMathType(getMathTypeFromEntry(entry)));
  let results = segments;

  if (peopleOnlyEntries || !eventsOnlyEntries) {
    results = results.filter(
      (segment) =>
        isCohortSegment(segment) ||
        (isUserSegment(segment) &&
          (!isBehaviorSegmentation(segment.user) || isAggregateUserFreqByGroupSegmentation(segment.user))) ||
        (isEventSegment(segment) && isExperimentSegment(segment.event)),
    );
  }
  if (hasXau) {
    results = results.filter((segment) => !(isBehaviorSegment(segment) || isMultiAttributionSegment(segment)));
  }
  if (hasCohort) {
    results = results.filter((segment) => {
      if (isCohortSegment(segment)) {
        return true;
      }
      if (isUserSegment(segment)) {
        // if dataGroupIds is provided, that means there are group cohorts
        // so we need to filter out segments that do not match the data group id
        for (const groupId of dataGroupIds) {
          if (groupId && segment.user.dataGroupId !== groupId) {
            return false;
          }
        }
        return true;
      }
      return false;
    });
  }

  // TODO remove this once metric-level data groups is fully rolled out. handled as part of sanitizeSelectionForDataGroups
  if (!hasMetricLevelDataGroups && (dataGroupId === undefined || dataGroupId === null)) {
    results = results.filter(
      (segment) => !isUserSegment(segment) || !isAggregateUserFreqByGroupSegmentation(segment.user),
    );
  }

  const nonInlineFormulaEntries = entries.filter((entry) => !isInlineFormulaEntry(entry));
  const everyMultiAttributionEventIsSupported = nonInlineFormulaEntries.every((entry) => {
    // We can ignore the case where entry.multiAttribution is blank. This is the
    // case when we are adding the multi attribution breakdown for the first
    // time, in which case we'll only add a compatible multi attribution type.
    const multiAttribution = getMultiAttributionFromEntry(entry);
    if (multiAttribution) {
      return isSupportedMultiAttributionTypeForEntry({
        entry,
        multiAttributionType: multiAttribution.type,
        metricsResource,
        behaviorsResource,
      });
    }
    return true;
  });

  // For multi attribution we want to remove the entire breakdown if any event
  // is not supported in the query.
  results = !everyMultiAttributionEventIsSupported
    ? results.filter((segment) => !isMultiAttributionSegment(segment))
    : results;

  return results;
}

/**
 * Sanitizes filter entries when a cohort entry is selected
 */
export function sanitizeFilters({
  entries,
  filters,
  metricsResource,
  behaviorsResource,
}: {
  entries: Array<QueryEntry | MetricEntry>;
  filters: PropertyFilter[];
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}) {
  const allEntries = flattenReferencedEntries(entries, metricsResource, behaviorsResource);
  const nonFormulaEntries = allEntries.filter(
    (entry) => entry.type !== TopLevelMetricType.NewFormulaEntry && !isFormulaEntry(entry),
  );
  const eventsOnlyEntries = nonFormulaEntries.every((entry) => isEventBasedEntry(entry) || isGroupedEntry(entry));
  const peopleOnlyEntries = nonFormulaEntries.length > 0 && nonFormulaEntries.every((entry) => isUserBasedEntry(entry));
  const hasXau = nonFormulaEntries.some((entry) => isXauMathType(getMathTypeFromEntry(entry)));
  let results = filters;
  if (hasXau) {
    results = filters.filter((segment) => !isBehaviorFilter(segment));
  }

  if (entries.some((entry) => isCohortEntry(entry))) {
    results = results.filter((x) => x.resourceType !== ResourceType.Event);
  }

  if (peopleOnlyEntries || !eventsOnlyEntries) {
    // TODO remove behavior stuff once it is no longer user resource type
    // only keep cohort filter and user property filters
    results = results.filter(
      (segment) =>
        isCohortFilter(segment) ||
        isExperimentSegment(segment) ||
        // For user behavior filters, we will only support PQA style behaviors here.  They should only be set when using group analytics
        (isUserFilter(segment) &&
          (!isBehaviorSegmentation(segment) || isAggregateUserFreqByGroupSegmentation(segment))),
    );
  }

  return results;
}

export type MismatchedFilterAndSegment = {
  filterLabel: string;
  segmentLabel: string;
};

/**
 * Ensure that any date range changes in the multi attribution breakdown are
 * applied to the filters.
 *
 * Remove filters that break the invariant that the multi attribution filter and
 * breakdown must have the same property.
 *
 * Returns filters with mismatched filters removed. It also return the pair of
 * labels corresponding to the first found mismatched filter and segment.
 */
export function sanitizeMultiAttributionFilter(
  filters: PropertyFilter[],
  groupBy: Segment[],
): {
  results: PropertyFilter[];
  mismatchedFilterAndSegment: MismatchedFilterAndSegment | null;
} {
  const multiAttributionSegment = groupBy.find((segment) => isMultiAttributionSegment(segment)) as EventSegment;
  const multiAttributionFilter = filters.find((filter) => isMultiAttributionFilter(filter));
  let mismatchedFilter = null;
  let results = filters.filter((filter) => !isMultiAttributionFilter(filter));

  function hasSamePropertyValue(val1: PropertyValue, val2: PropertyValue) {
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length === val2.length) {
        return val1.every((el, index) => el === val2[index]);
      }
      return false;
    }
    return val1 === val2;
  }
  if (
    multiAttributionSegment &&
    multiAttributionFilter &&
    !hasSamePropertyValue(
      multiAttributionSegment[ResourceType.Event].behavior?.property.value,
      multiAttributionFilter.behavior?.property.value,
    )
  ) {
    // Attribution filter must be defined on the same property as the Attribution breakdown.
    mismatchedFilter = {
      filterLabel: multiAttributionFilter.behavior?.property.label,
      segmentLabel: multiAttributionSegment[ResourceType.Event].behavior?.property.label,
    };
  } else if (multiAttributionSegment[ResourceType.Event].behavior) {
    // Align all Attribution filters behaviors to the Attribution breakdown behavior, in
    // case there were any updates to the latter.
    // All attribution behaviors should always be aligned.
    results = filters.map((filter) => {
      return isMultiAttributionFilter(filter)
        ? Object.assign({}, filter, {
            behavior: multiAttributionSegment[ResourceType.Event].behavior,
          })
        : filter;
    });
  }

  return {
    results,
    mismatchedFilterAndSegment: mismatchedFilter,
  };
}

function isCoreReport() {
  return [`dashboards`, `funnels`, `insights`, `retention`, `flows`, `metric-tree`, `experiments`].includes(
    getReportName(),
  );
}

/** TODO(pablo): drop this method once internal testing is completed */
export const qbStyleAttrs = (forNewUI = null) => ({
  selectorTheme: forNewUI ?? isCoreReport() ? ButtonTheme.QueryBuilder : ButtonTheme.Select,
  selectorType: `button`,
  buttonTheme: forNewUI ?? isCoreReport() ? ButtonTheme.Tertiary : ButtonTheme.Select,
});

export function isXauMathType(value: MATH_TYPE): value is XAU_MATH_TYPES {
  return Object.values(XAU_MATH_TYPES).includes(value as XAU_MATH_TYPES);
}

export function getFunnelStepCount(entries: Entry[]): number | undefined {
  const funnelEntries = entries.filter((entry) => isMetricEntry(entry) && entry.behavior.type === MetricType.Funnel);
  if (funnelEntries.length > 0) {
    const funnelBehaviors = funnelEntries.map((entry) => (entry as MetricEntry).behavior.behaviors);
    const funnelLengths = funnelBehaviors.map((behaviors) => behaviors.length);
    return Math.min(...funnelLengths);
  }
  return undefined;
}

/** Get entries with any saved formula referencedMetrics appended */
export function flattenReferencedEntries(
  entries: Array<QueryEntry | MetricEntry>,
  metricsResource: MetricsResource,
  behaviorsResource: BehaviorsResource,
): Array<QueryEntry | MetricEntry> {
  if (!entries?.length) {
    return entries;
  }

  const entriesFromFormulas = entries
    .map((entry) => {
      if (entry.type === TopLevelMetricType.NewFormulaEntry) {
        return entry.referencedMetrics;
      }

      // @todo (metrics) marked for deletion
      if (isFormulaEntry(entry) && (entry.event.id || entry.event.referencedMetrics)) {
        const resolvedEntry = getResolvedFormulaEntry({formulaEntry: entry, metricsResource, behaviorsResource});
        return resolvedEntry && getEntries({showClauses: resolvedEntry.event.referencedMetrics || []});
      }

      return [];
    })
    .filter(Boolean)
    .flat();

  return [...entries, ...entriesFromFormulas];
}
